import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  ClipboardCheck,
  Clock3,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  ShieldAlert,
  Target,
  Trash2,
  Trophy,
  Undo2,
  Users,
} from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { useAuth } from '../state/AuthContext';
import { useStore } from '../hooks/useStore';
import {
  addGoal,
  addPenalty,
  addScorekeeperNote,
  addShotOnGoal,
  addShootoutAttempt,
  buildSubmitChecklist,
  canProfileScoreGame,
  createGameArchiveMetadata,
  deleteScoreEvent,
  getActiveScoringSession,
  getLiveScore,
  getResultForGame,
  heartbeatScoringSession,
  listScoreEvents,
  releaseScoringSession,
  reopenGame,
  setGameAttendance,
  startGame,
  startScoringSession,
  submitForfeitResult,
  submitGameResult,
  type AttendanceStatus,
  updateGoal,
  updatePenalty,
} from '../services/scoringService';
import { clockToGameSecond, gameSecondToClock, resolvePeriodLengthMinutes } from '../services/scoringEngine';
import { getDb } from '../services/store';
import type { GameScoringSession, Id, RosterEntry, ScoreEvent, Team, TournamentSettings } from '../models';

type GoalContext = 'even' | 'power_play' | 'short_handed' | 'empty_net';
type ScoreTab = 'scoresheet' | 'attendance' | 'events' | 'final';
type FinalDecision = 'regulation' | 'overtime' | 'shootout' | 'forfeit';

export function ScoreGamePage() {
  const { gameId = '' } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const db = useStore((d) => d);
  const { profile, canAdmin } = useAuth();
  const [tab, setTab] = useState<ScoreTab>('scoresheet');
  const [period, setPeriod] = useState(1);
  const [clockMinute, setClockMinute] = useState('');
  const [clockSecond, setClockSecond] = useState('');
  const [goalTeamId, setGoalTeamId] = useState('');
  const [goalScorerId, setGoalScorerId] = useState('');
  const [goalAssistId, setGoalAssistId] = useState('');
  const [goalContext, setGoalContext] = useState<GoalContext>('even');
  const [penaltyTeamId, setPenaltyTeamId] = useState('');
  const [penaltyPlayerId, setPenaltyPlayerId] = useState('');
  const [penaltyType, setPenaltyType] = useState('Tripping');
  const [penaltyMinutes, setPenaltyMinutes] = useState(2);
  const [shotTeamId, setShotTeamId] = useState('');
  const [shotPlayerId, setShotPlayerId] = useState('');
  const [shotGoalieId, setShotGoalieId] = useState('');
  const [shotSaved, setShotSaved] = useState(true);
  const [shootoutTeamId, setShootoutTeamId] = useState('');
  const [shootoutShooterId, setShootoutShooterId] = useState('');
  const [shootoutGoalieId, setShootoutGoalieId] = useState('');
  const [shootoutMade, setShootoutMade] = useState(true);
  const [note, setNote] = useState('');
  const [decidedBy, setDecidedBy] = useState<FinalDecision>('regulation');
  const [winningTeamId, setWinningTeamId] = useState('');
  const [editingGoalId, setEditingGoalId] = useState('');
  const [editingPenaltyId, setEditingPenaltyId] = useState('');
  const [actionState, setActionState] = useState<{ pending?: string; success?: string; error?: string }>({});

  const game = db.games.find((g) => g.id === gameId);
  const tournament = game ? db.tournaments.find((t) => t.id === game.tournamentId) : undefined;
  const home = game ? db.teams.find((t) => t.id === game.homeTeamId) : undefined;
  const away = game ? db.teams.find((t) => t.id === game.awayTeamId) : undefined;
  const teams = [home, away].filter(Boolean) as Team[];
  const score = getLiveScore(gameId);
  const events = listScoreEvents(gameId);
  const shootoutAttempts = events.filter((event) => event.type === 'shootout_attempt');
  const result = getResultForGame(gameId);
  const activeSession = getActiveScoringSession(gameId);
  const ownedSession = activeSession?.profileId === profile.id ? activeSession : undefined;
  const canScore = canAdmin || canProfileScoreGame(profile, gameId);
  const isFinal = game?.status === 'final' || game?.status === 'forfeit';
  const sessionBlocked = Boolean(activeSession && activeSession.profileId !== profile.id && !isFinal);
  const canWriteScores = !isFinal && Boolean(ownedSession) && !sessionBlocked;
  const writeDisabled = !canWriteScores || Boolean(actionState.pending);
  const periodLengthMinutes = tournament ? resolvePeriodLengthMinutes(tournament.settings.scoring) : 20;
  const gameSecond = toGameSecond(period, clockMinute, clockSecond, periodLengthMinutes);
  const tied = score.home === score.away;
  const finalRequiresWinner = decidedBy === 'forfeit' || (tied && decidedBy !== 'regulation');
  const checklist = buildSubmitChecklist(gameId, {
    decidedBy,
    winningTeamId: finalRequiresWinner ? winningTeamId : undefined,
  });
  const nextShootoutAttemptNumber = shootoutAttempts.length + 1;
  const nextShootoutRound = Math.max(1, Math.ceil(nextShootoutAttemptNumber / 2));
  const shootoutRoundCount = tournament?.settings.scoring.shootoutRoundCount ?? 3;
  const shootoutIsSuddenDeath = nextShootoutRound > shootoutRoundCount;
  const tieGuidance = tournament ? tieResolutionGuidance(tournament.settings.scoring.overtimeFormat) : undefined;

  useEffect(() => {
    if (!ownedSession || isFinal) return;
    const timer = window.setInterval(() => heartbeatScoringSession(ownedSession.sessionId), 45_000);
    return () => window.clearInterval(timer);
  }, [ownedSession?.sessionId, isFinal]);

  useEffect(() => {
    if (!game) return;
    setGoalTeamId((value) => value || game.homeTeamId);
    setPenaltyTeamId((value) => value || game.homeTeamId);
    setShotTeamId((value) => value || game.homeTeamId);
    setShootoutTeamId((value) => value || game.homeTeamId);
    setWinningTeamId((value) => value || game.homeTeamId);
  }, [game?.id]);

  useEffect(() => {
    if (!game || decidedBy !== 'shootout') return;
    const homeGoals = shootoutAttempts.filter((event) => event.teamId === game.homeTeamId && (event.shootoutMade ?? event.notes === 'made')).length;
    const awayGoals = shootoutAttempts.filter((event) => event.teamId === game.awayTeamId && (event.shootoutMade ?? event.notes === 'made')).length;
    if (homeGoals > awayGoals) setWinningTeamId(game.homeTeamId);
    if (awayGoals > homeGoals) setWinningTeamId(game.awayTeamId);
  }, [decidedBy, game?.id, shootoutAttempts.length]);

  const rosterByTeam = useMemo(() => {
    const map = new Map<Id, RosterEntry[]>();
    if (!game) return map;
    for (const team of teams) {
      map.set(team.id, db.rosterEntries.filter((entry) => entry.teamId === team.id));
    }
    return map;
  }, [db.rosterEntries, game?.id, teams.map((team) => team.id).join('|')]);

  if (!game || !home || !away || !tournament) {
    return (
      <div className="empty">
        <p>Game not found.</p>
        <Link to="/admin" className="btn btn--ghost"><ArrowLeft size={14} /> Back</Link>
      </div>
    );
  }

  if (!canScore) {
    return (
      <div className="empty">
        <ShieldAlert size={22} />
        <p>You are not assigned to score this game.</p>
        <Link to={`/events/${tournament.slug}`} className="btn btn--ghost"><ArrowLeft size={14} /> Back to event</Link>
      </div>
    );
  }

  const sessionMeta = {
    sourceSessionId: ownedSession?.sessionId,
    createdByProfileId: profile.id,
  };

  const runScoreAction = (pending: string, success: string, action: () => void) => {
    setActionState({ pending });
    try {
      action();
      setActionState({ success });
      window.setTimeout(() => {
        setActionState((current) => (current.success === success ? {} : current));
      }, 1800);
    } catch (error) {
      setActionState({
        error: error instanceof Error ? error.message : 'The scoring action did not save.',
      });
    }
  };

  const addGoalFromForm = () => {
    if (!goalTeamId || !canWriteScores) return;
    runScoreAction(editingGoalId ? 'Saving goal...' : 'Recording goal...', editingGoalId ? 'Goal updated.' : 'Goal recorded.', () => {
      const input = {
        teamId: goalTeamId,
        scorerPlayerId: goalScorerId || undefined,
        assistPlayerId: goalAssistId && goalAssistId !== goalScorerId ? goalAssistId : undefined,
        period,
        gameSecond,
        isPowerPlay: goalContext === 'power_play',
        isShortHanded: goalContext === 'short_handed',
        isEmptyNet: goalContext === 'empty_net',
        ...sessionMeta,
      };
      if (editingGoalId) updateGoal({ goalEventId: editingGoalId, ...input });
      else addGoal({ gameId, ...input });
      setEditingGoalId('');
      setGoalScorerId('');
      setGoalAssistId('');
      setGoalContext('even');
    });
  };

  const addPenaltyFromForm = () => {
    if (!penaltyTeamId || !canWriteScores) return;
    runScoreAction(editingPenaltyId ? 'Saving penalty...' : 'Recording penalty...', editingPenaltyId ? 'Penalty updated.' : 'Penalty recorded.', () => {
      const input = {
        teamId: penaltyTeamId,
        playerId: penaltyPlayerId || undefined,
        penaltyType,
        penaltyMinutes,
        penaltyClass: penaltyMinutes >= 10 ? 'misconduct' as const : penaltyMinutes >= 5 ? 'major' as const : 'minor' as const,
        period,
        gameSecond,
        ...sessionMeta,
      };
      if (editingPenaltyId) updatePenalty({ penaltyEventId: editingPenaltyId, ...input });
      else addPenalty({ gameId, ...input });
      setEditingPenaltyId('');
      setPenaltyPlayerId('');
    });
  };

  const addShotFromForm = () => {
    if (!shotTeamId || !canWriteScores) return;
    runScoreAction('Recording shot...', 'Shot recorded.', () => {
      addShotOnGoal({
        gameId,
        teamId: shotTeamId,
        playerId: shotPlayerId || undefined,
        goalieId: shotGoalieId || undefined,
        saved: shotSaved,
        period,
        gameSecond,
        ...sessionMeta,
      });
      setShotPlayerId('');
      setShotGoalieId('');
      setShotSaved(true);
    });
  };

  const addShootoutFromForm = () => {
    if (!shootoutTeamId || !canWriteScores) return;
    runScoreAction('Recording shootout attempt...', 'Shootout attempt recorded.', () => {
      addShootoutAttempt({
        gameId,
        teamId: shootoutTeamId,
        shooterPlayerId: shootoutShooterId || undefined,
        goalieId: shootoutGoalieId || undefined,
        made: shootoutMade,
        round: nextShootoutRound,
        attemptNumber: nextShootoutAttemptNumber,
        suddenDeath: shootoutIsSuddenDeath,
        ...sessionMeta,
      });
      setShootoutShooterId('');
      setShootoutGoalieId('');
      setShootoutMade(true);
    });
  };

  const shotGoalieRoster =
    shotTeamId === game.homeTeamId
      ? rosterByTeam.get(game.awayTeamId) ?? []
      : rosterByTeam.get(game.homeTeamId) ?? [];
  const shootoutGoalieRoster =
    shootoutTeamId === game.homeTeamId
      ? rosterByTeam.get(game.awayTeamId) ?? []
      : rosterByTeam.get(game.homeTeamId) ?? [];

  const submitFinal = () => {
    if (!canWriteScores) return;
    runScoreAction('Submitting final...', 'Final submitted.', () => {
      const finalWinner = finalRequiresWinner ? winningTeamId || undefined : undefined;
      const submitted =
        decidedBy === 'forfeit'
          ? finalWinner
            ? submitForfeitResult({ gameId, winningTeamId: finalWinner, submittedByProfileId: profile.id })
            : undefined
          : submitGameResult({
              gameId,
              decidedBy,
              winningTeamId: finalWinner,
              submittedByProfileId: profile.id,
            });
      if (!submitted) throw new Error('Final result is blocked. Review the submit checklist.');
      void archiveFinalGame(gameId).catch(() => createGameArchiveMetadata({ gameId, createdByProfileId: profile.id }));
      if (ownedSession) releaseScoringSession(ownedSession.sessionId);
    });
  };

  const cancelGoalEdit = () => {
    setEditingGoalId('');
    setGoalScorerId('');
    setGoalAssistId('');
    setGoalContext('even');
  };

  const cancelPenaltyEdit = () => {
    setEditingPenaltyId('');
    setPenaltyPlayerId('');
    setPenaltyType('Tripping');
    setPenaltyMinutes(2);
  };

  const loadClockFromEvent = (event: ScoreEvent) => {
    setPeriod(event.period ?? 1);
    const clock = gameSecondToClock(event.gameSecond, periodLengthMinutes);
    setClockMinute(clock ? String(clock.minute) : '');
    setClockSecond(clock ? String(clock.second).padStart(2, '0') : '');
  };

  const beginGoalEdit = (event: ScoreEvent) => {
    const assist = events.find((row) => row.parentEventId === event.id && row.type === 'assist');
    setEditingGoalId(event.id);
    setEditingPenaltyId('');
    setGoalTeamId(event.teamId ?? game.homeTeamId);
    setGoalScorerId(event.playerId ?? '');
    setGoalAssistId(assist?.playerId ?? '');
    setGoalContext(event.isPowerPlay ? 'power_play' : event.isShortHanded ? 'short_handed' : event.isEmptyNet ? 'empty_net' : 'even');
    loadClockFromEvent(event);
    setTab('scoresheet');
  };

  const beginPenaltyEdit = (event: ScoreEvent) => {
    setEditingPenaltyId(event.id);
    setEditingGoalId('');
    setPenaltyTeamId(event.teamId ?? game.homeTeamId);
    setPenaltyPlayerId(event.playerId ?? '');
    setPenaltyType(event.penaltyType ?? 'Penalty');
    setPenaltyMinutes(event.penaltyMinutes ?? 2);
    loadClockFromEvent(event);
    setTab('scoresheet');
  };

  return (
    <>
      <ModuleHero
        eyebrow={tournament.format === 'season' ? 'Season scoring' : 'Tournament scoring'}
        title={`${home.name} vs ${away.name}`}
        description="Capture the full game sheet: attendance, goals, assists, shots, penalties, and final result."
        icon={Trophy}
        accent="goal"
        status={
          isFinal
            ? { label: game.status === 'forfeit' ? 'Forfeit' : 'Final', tone: 'ok' }
            : game.status === 'in_progress'
              ? { label: 'Live scoring', tone: 'accent' }
              : { label: 'Ready to score', tone: 'info' }
        }
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn--ghost btn--sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
          <Link to={`/events/${tournament.slug}/games/${game.id}`} className="btn btn--ghost btn--sm">
            Public game center
          </Link>
          {!isFinal && game.status !== 'in_progress' && (
            <button
              className="btn btn--primary btn--sm"
              disabled={sessionBlocked}
              onClick={() => {
                if (!ownedSession) {
                  startScoringSession({ gameId, profileId: profile.id, deviceLabel: browserLabel() });
                }
                startGame(game.id);
              }}
            >
              <Play size={14} /> Start game
            </button>
          )}
        </div>
      </ModuleHero>

      {!isFinal && (
        <SessionBanner
          session={activeSession}
          owned={Boolean(ownedSession)}
          profileName={profile.displayName}
          locked={sessionBlocked}
          onStart={() => startScoringSession({ gameId, profileId: profile.id, deviceLabel: browserLabel() })}
          onTakeover={() => startScoringSession({ gameId, profileId: profile.id, forceTakeover: true, deviceLabel: browserLabel() })}
          onRelease={() => {
            if (ownedSession) releaseScoringSession(ownedSession.sessionId);
          }}
        />
      )}

      <ScoreActionStatus state={actionState} />

      <section className="scoresheet-scoreboard">
        <ScoreboardTeam team={home} score={score.home} shots={events.filter((event) => event.teamId === home.id && event.type === 'shot_on_goal').length} />
        <div className="scoresheet-scoreboard__middle">
          <span className="chip chip--ok"><span className="chip__dot" /> {game.status.replace('_', ' ')}</span>
          <div className="scoresheet-scoreboard__clock">P{period} {clockMinute || '--'}:{String(clockSecond || '--').padStart(2, '0')}</div>
        </div>
        <ScoreboardTeam team={away} score={score.away} shots={events.filter((event) => event.teamId === away.id && event.type === 'shot_on_goal').length} align="right" />
      </section>

      {!isFinal && tied && tieGuidance && (
        <div className="note note--info scoresheet-tie-guide">
          <CircleAlert size={16} />
          <div>
            <strong>{tieGuidance.title}</strong>
            <p>{tieGuidance.body}</p>
          </div>
          {tieGuidance.decision === 'shootout' && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setDecidedBy('shootout');
                setTab('scoresheet');
              }}
            >
              Go to shootout entry
            </button>
          )}
          {tieGuidance.decision === 'overtime' && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setDecidedBy('overtime');
                setPeriod((value) => Math.max(value, (tournament.settings.scoring.periodCount ?? 3) + 1));
              }}
            >
              Set OT period
            </button>
          )}
        </div>
      )}

      <div className="scoresheet-tabs" role="tablist" aria-label="Scoresheet sections">
        {[
          ['scoresheet', 'Scoresheet', Target],
          ['attendance', 'Attendance', Users],
          ['events', 'Event log', Clock3],
          ['final', 'Submit final', Lock],
        ].map(([id, label, Icon]) => (
          <button
            key={id as string}
            type="button"
            className={`scoresheet-tabs__tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => setTab(id as ScoreTab)}
          >
            <Icon size={16} /> {label as string}
          </button>
        ))}
      </div>

      {tab === 'scoresheet' && (
        <div className="grid grid--2">
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Game context</h3>
                <p className="card__sub">Period and clock attach to the next score event.</p>
              </div>
            </div>
            <div className="grid grid--3">
              <label>Period<input className="input" type="number" min={1} max={8} value={period} onChange={(event) => setPeriod(Math.max(1, Number(event.target.value || 1)))} disabled={writeDisabled} /></label>
              <label>Minute<input className="input" type="number" min={0} max={periodLengthMinutes} value={clockMinute} onChange={(event) => setClockMinute(event.target.value)} placeholder="12" disabled={writeDisabled} /></label>
              <label>Second<input className="input" type="number" min={0} max={59} value={clockSecond} onChange={(event) => setClockSecond(event.target.value)} placeholder="00" disabled={writeDisabled} /></label>
            </div>
            <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
              This event uses {periodLengthMinutes}-minute period timing from the event settings.
            </p>
          </div>

          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Shots</h3>
                <p className="card__sub">Tap to record a shot on goal.</p>
              </div>
            </div>
            <div className="grid grid--2">
              {teams.map((team) => (
                <button
                  key={team.id}
                  className="btn btn--ghost scoresheet-shot"
                  disabled={writeDisabled}
                  onClick={() => runScoreAction('Recording shot...', 'Shot recorded.', () => addShotOnGoal({ gameId, teamId: team.id, period, gameSecond, ...sessionMeta }))}
                >
                  <Target size={16} /> {team.shortCode} shot
                </button>
              ))}
            </div>
            <div className="scoresheet-shot-detail">
              <label>
                Shooting team
                <select
                  className="select"
                  value={shotTeamId}
                  disabled={writeDisabled}
                  onChange={(event) => {
                    setShotTeamId(event.target.value);
                    setShotPlayerId('');
                    setShotGoalieId('');
                  }}
                >
                  {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <div className="grid grid--2">
                <label>
                  Shooter
                  <PlayerSelect
                    roster={rosterByTeam.get(shotTeamId) ?? []}
                    value={shotPlayerId}
                    onChange={setShotPlayerId}
                    disabled={writeDisabled}
                    optionalLabel="No shooter selected"
                  />
                </label>
                <label>
                  Goalie credited
                  <PlayerSelect
                    roster={shotGoalieRoster}
                    value={shotGoalieId}
                    onChange={setShotGoalieId}
                    disabled={writeDisabled}
                    optionalLabel="No goalie selected"
                  />
                </label>
              </div>
              <label className="scoresheet-check">
                <input type="checkbox" checked={shotSaved} onChange={(event) => setShotSaved(event.target.checked)} disabled={writeDisabled} />
                Count as goalie save when a goalie is selected
              </label>
              <button className={`btn btn--primary ${actionState.pending === 'Recording shot...' ? 'is-loading' : ''}`} disabled={writeDisabled} onClick={addShotFromForm} aria-busy={actionState.pending === 'Recording shot...'}>
                {actionState.pending === 'Recording shot...' ? <Loader2 size={14} className="spin" /> : <Target size={14} />}
                Record detailed shot
              </button>
            </div>
          </div>

          <ScoreFormCard
            title="Add goal"
            subtitle="Scorer and assist are optional but recommended."
            teamId={goalTeamId}
            setTeamId={(teamId) => {
              setGoalTeamId(teamId);
              setGoalScorerId('');
              setGoalAssistId('');
            }}
            teams={teams}
            roster={rosterByTeam.get(goalTeamId) ?? []}
            primaryPlayerId={goalScorerId}
            setPrimaryPlayerId={setGoalScorerId}
            secondaryPlayerId={goalAssistId}
            setSecondaryPlayerId={setGoalAssistId}
            primaryLabel="Scorer"
            secondaryLabel="Assist"
            actionLabel={editingGoalId ? 'Save goal' : 'Record goal'}
            disabled={writeDisabled}
            loading={actionState.pending === (editingGoalId ? 'Saving goal...' : 'Recording goal...')}
            onSubmit={addGoalFromForm}
          >
            {editingGoalId && (
              <div className="note note--info row row--between">
                Editing a goal. Save changes or cancel to record a new goal.
                <button type="button" className="btn btn--ghost btn--sm" onClick={cancelGoalEdit}>Cancel edit</button>
              </div>
            )}
            <label>
              Goal type
              <select className="select" value={goalContext} onChange={(event) => setGoalContext(event.target.value as GoalContext)} disabled={writeDisabled}>
                <option value="even">Even strength</option>
                <option value="power_play">Power play</option>
                <option value="short_handed">Short-handed</option>
                <option value="empty_net">Empty net</option>
              </select>
            </label>
          </ScoreFormCard>

          <ScoreFormCard
            title="Add penalty"
            subtitle="Capture the player, infraction, and minutes."
            teamId={penaltyTeamId}
            setTeamId={(teamId) => {
              setPenaltyTeamId(teamId);
              setPenaltyPlayerId('');
            }}
            teams={teams}
            roster={rosterByTeam.get(penaltyTeamId) ?? []}
            primaryPlayerId={penaltyPlayerId}
            setPrimaryPlayerId={setPenaltyPlayerId}
            primaryLabel="Player"
            actionLabel={editingPenaltyId ? 'Save penalty' : 'Record penalty'}
            disabled={writeDisabled}
            loading={actionState.pending === (editingPenaltyId ? 'Saving penalty...' : 'Recording penalty...')}
            onSubmit={addPenaltyFromForm}
          >
            {editingPenaltyId && (
              <div className="note note--info row row--between">
                Editing a penalty. Save changes or cancel to record a new penalty.
                <button type="button" className="btn btn--ghost btn--sm" onClick={cancelPenaltyEdit}>Cancel edit</button>
              </div>
            )}
            <div className="grid grid--2">
              <label>Infraction<input className="input" value={penaltyType} onChange={(event) => setPenaltyType(event.target.value)} disabled={writeDisabled} /></label>
              <label>Minutes<input className="input" type="number" min={1} value={penaltyMinutes} onChange={(event) => setPenaltyMinutes(Math.max(1, Number(event.target.value || 2)))} disabled={writeDisabled} /></label>
            </div>
          </ScoreFormCard>

          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Shootout attempts</h3>
                <p className="card__sub">
                  Track each shooter and goalie. Shootout attempts decide the official result but do not count as player goals or points.
                </p>
              </div>
              <span className="chip">
                Round {nextShootoutRound}{shootoutIsSuddenDeath ? ' SD' : ''} - Attempt {nextShootoutAttemptNumber}
              </span>
            </div>
            <div className="stack">
              <label>
                Shooting team
                <select
                  className="select"
                  value={shootoutTeamId}
                  onChange={(event) => {
                    setShootoutTeamId(event.target.value);
                    setShootoutShooterId('');
                    setShootoutGoalieId('');
                  }}
                  disabled={writeDisabled}
                >
                  {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <div className="grid grid--2">
                <label>
                  Shooter
                  <PlayerSelect
                    roster={rosterByTeam.get(shootoutTeamId) ?? []}
                    value={shootoutShooterId}
                    onChange={setShootoutShooterId}
                    disabled={writeDisabled}
                    optionalLabel="No shooter selected"
                  />
                </label>
                <label>
                  Goalie faced
                  <PlayerSelect
                    roster={shootoutGoalieRoster}
                    value={shootoutGoalieId}
                    onChange={setShootoutGoalieId}
                    disabled={writeDisabled}
                    optionalLabel="No goalie selected"
                  />
                </label>
              </div>
              <div className="segmented-control" role="group" aria-label="Shootout attempt result">
                <button
                  type="button"
                  className={shootoutMade ? 'is-active' : ''}
                  disabled={writeDisabled}
                  onClick={() => setShootoutMade(true)}
                >
                  Goal
                </button>
                <button
                  type="button"
                  className={!shootoutMade ? 'is-active' : ''}
                  disabled={writeDisabled}
                  onClick={() => setShootoutMade(false)}
                >
                  Save / miss
                </button>
              </div>
              <button className={`btn btn--primary ${actionState.pending === 'Recording shootout attempt...' ? 'is-loading' : ''}`} disabled={writeDisabled} onClick={addShootoutFromForm} aria-busy={actionState.pending === 'Recording shootout attempt...'}>
                {actionState.pending === 'Recording shootout attempt...' ? <Loader2 size={14} className="spin" /> : <Target size={14} />}
                Record shootout attempt
              </button>
              {shootoutAttempts.length > 0 && (
                <div className="note note--info">
                  Shootout: {home.shortCode} {shootoutAttempts.filter((event) => event.teamId === home.id && (event.shootoutMade ?? event.notes === 'made')).length}
                  {' - '}
                  {away.shortCode} {shootoutAttempts.filter((event) => event.teamId === away.id && (event.shootoutMade ?? event.notes === 'made')).length}
                  {' '}on {shootoutAttempts.length} attempt{shootoutAttempts.length === 1 ? '' : 's'}.
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Scorekeeper note</h3>
                <p className="card__sub">For corrections, delays, equipment issues, or unusual plays.</p>
              </div>
            </div>
            <textarea className="input" rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note..." disabled={writeDisabled} />
            <button
              className="btn btn--ghost btn--sm"
              style={{ marginTop: 10 }}
              disabled={writeDisabled || !note.trim()}
              onClick={() => {
                runScoreAction('Saving note...', 'Note added.', () => {
                  addScorekeeperNote({ gameId, notes: note.trim(), period, gameSecond, ...sessionMeta });
                  setNote('');
                });
              }}
            >
              Add note
            </button>
          </div>
        </div>
      )}

      {tab === 'attendance' && (
        <div className="grid grid--2">
          {teams.map((team) => (
            <AttendanceCard
              key={team.id}
              team={team}
              roster={rosterByTeam.get(team.id) ?? []}
              gameId={gameId}
              disabled={!canWriteScores}
              saving={Boolean(actionState.pending)}
            />
          ))}
        </div>
      )}

      {tab === 'events' && (
        <EventLog events={events} locked={!canWriteScores} onEditGoal={beginGoalEdit} onEditPenalty={beginPenaltyEdit} periodLengthMinutes={periodLengthMinutes} />
      )}

      {tab === 'final' && (
        <div className="grid grid--2">
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Final result</h3>
                <p className="card__sub">This locks the official score and updates standings.</p>
              </div>
            </div>
            <div className="scoresheet-final-score">
              <span>{home.shortCode}</span>
              <strong>{score.home}</strong>
              <span>{away.shortCode}</span>
              <strong>{score.away}</strong>
            </div>
            {shootoutAttempts.length > 0 && (
              <div className="note note--info" style={{ marginTop: 12 }}>
                Shootout attempts: {home.shortCode} {shootoutAttempts.filter((event) => event.teamId === home.id && (event.shootoutMade ?? event.notes === 'made')).length}
                {' - '}
                {away.shortCode} {shootoutAttempts.filter((event) => event.teamId === away.id && (event.shootoutMade ?? event.notes === 'made')).length}.
              </div>
            )}
            <div className="grid grid--2" style={{ marginTop: 14 }}>
              <label>
                Decided by
                <select className="select" value={decidedBy} onChange={(event) => setDecidedBy(event.target.value as typeof decidedBy)} disabled={writeDisabled}>
                  <option value="regulation">Regulation / regular result</option>
                  <option value="overtime">Overtime</option>
                  <option value="shootout">Shootout</option>
                  <option value="forfeit">Forfeit</option>
                </select>
              </label>
              <label>
                {decidedBy === 'forfeit' ? 'Forfeit winner' : 'Winner'}
                <select className="select" value={winningTeamId} onChange={(event) => setWinningTeamId(event.target.value)} disabled={writeDisabled || !finalRequiresWinner}>
                  {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
            </div>
            {tied && (decidedBy === 'overtime' || decidedBy === 'shootout') && (
              <div className="note note--info" style={{ marginTop: 12 }}>
                The official final score will add one goal to the selected OT/shootout winner. Shootout attempts are tracked separately and do not count as goals or points.
              </div>
            )}
            {decidedBy === 'forfeit' && (
              <div className="note note--info" style={{ marginTop: 12 }}>
                A forfeit records a 1-0 official result for the selected team and closes the game without scoring events.
              </div>
            )}
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              {!isFinal && (
                <button className={`btn btn--primary ${actionState.pending === 'Submitting final...' ? 'is-loading' : ''}`} disabled={writeDisabled || checklist.blockers.length > 0} onClick={submitFinal} aria-busy={actionState.pending === 'Submitting final...'}>
                  {actionState.pending === 'Submitting final...' ? <Loader2 size={14} className="spin" /> : <Lock size={14} />}
                  Submit final
                </button>
              )}
              {isFinal && canAdmin && (
                <button className="btn btn--ghost" onClick={() => reopenGame(gameId)}>
                  <Undo2 size={14} /> Reopen game
                </button>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Submit checklist</h3>
                <p className="card__sub">Blockers must be fixed. Warnings are review prompts.</p>
              </div>
            </div>
            <Checklist checklist={checklist} />
            {result && (
              <div className="note note--ok" style={{ marginTop: 12 }}>
                Official result submitted at {new Date(result.endedAt).toLocaleString()}.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function SessionBanner({
  session,
  owned,
  profileName,
  locked,
  onStart,
  onTakeover,
  onRelease,
}: {
  session?: GameScoringSession;
  owned: boolean;
  profileName: string;
  locked: boolean;
  onStart: () => void;
  onTakeover: () => void;
  onRelease: () => void;
}) {
  if (!session) {
    return (
      <div className="note note--info scoresheet-session">
        <RefreshCw size={16} /> No active scoring session.
        <button className="btn btn--ghost btn--sm" onClick={onStart}>Start session</button>
      </div>
    );
  }
  if (owned) {
    return (
      <div className="note note--ok scoresheet-session">
        <CheckCircle2 size={16} /> {profileName} is controlling this scoresheet. Session expires if the page is inactive.
        <button className="btn btn--ghost btn--sm" onClick={onRelease}>Release</button>
      </div>
    );
  }
  return (
    <div className="note note--warn scoresheet-session">
      <CircleAlert size={16} />
      Another scorer is currently active. You can take over if their session is stale or they handed you the table.
      {locked && <button className="btn btn--ghost btn--sm" onClick={onTakeover}>Take over</button>}
    </div>
  );
}

function ScoreActionStatus({ state }: { state: { pending?: string; success?: string; error?: string } }) {
  if (!state.pending && !state.success && !state.error) return null;
  if (state.pending) {
    return (
      <div className="note note--info scoresheet-action-status" role="status" aria-live="polite">
        <Loader2 size={16} className="spin" /> {state.pending}
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="note note--warn scoresheet-action-status" role="alert">
        <CircleAlert size={16} /> {state.error}
      </div>
    );
  }
  return (
    <div className="note note--ok scoresheet-action-status" role="status" aria-live="polite">
      <CheckCircle2 size={16} /> {state.success}
    </div>
  );
}

function ScoreboardTeam({ team, score, shots, align }: { team: Team; score: number; shots: number; align?: 'right' }) {
  return (
    <div className={`scoresheet-scoreboard__team ${align === 'right' ? 'is-right' : ''}`}>
      <span style={{ background: team.primaryColor }} />
      <div>
        <div className="scoresheet-scoreboard__name">{team.name}</div>
        <div className="muted">{shots} shots</div>
      </div>
      <strong>{score}</strong>
    </div>
  );
}

function ScoreFormCard({
  title,
  subtitle,
  teamId,
  setTeamId,
  teams,
  roster,
  primaryPlayerId,
  setPrimaryPlayerId,
  secondaryPlayerId,
  setSecondaryPlayerId,
  primaryLabel,
  secondaryLabel,
  actionLabel,
  disabled,
  loading,
  onSubmit,
  children,
}: {
  title: string;
  subtitle: string;
  teamId: string;
  setTeamId: (id: string) => void;
  teams: Team[];
  roster: RosterEntry[];
  primaryPlayerId: string;
  setPrimaryPlayerId: (id: string) => void;
  secondaryPlayerId?: string;
  setSecondaryPlayerId?: (id: string) => void;
  primaryLabel: string;
  secondaryLabel?: string;
  actionLabel: string;
  disabled: boolean;
  loading?: boolean;
  onSubmit: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">{title}</h3>
          <p className="card__sub">{subtitle}</p>
        </div>
      </div>
      <div className="stack">
        <label>
          Team
          <select className="select" value={teamId} onChange={(event) => setTeamId(event.target.value)} disabled={disabled}>
            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
          </select>
        </label>
        <label>
          {primaryLabel}
          <PlayerSelect roster={roster} value={primaryPlayerId} onChange={setPrimaryPlayerId} disabled={disabled} />
        </label>
        {setSecondaryPlayerId && (
          <label>
            {secondaryLabel}
            <PlayerSelect roster={roster.filter((entry) => entry.playerId !== primaryPlayerId)} value={secondaryPlayerId ?? ''} onChange={setSecondaryPlayerId} disabled={disabled} optionalLabel="No assist" />
          </label>
        )}
        {children}
        <button className={`btn btn--primary ${loading ? 'is-loading' : ''}`} disabled={disabled} onClick={onSubmit} aria-busy={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

function PlayerSelect({ roster, value, onChange, disabled, optionalLabel = 'No player' }: { roster: RosterEntry[]; value: string; onChange: (id: string) => void; disabled: boolean; optionalLabel?: string }) {
  const db = getDb();
  return (
    <select className="select" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
      <option value="">{optionalLabel}</option>
      {roster.map((entry) => {
        const player = db.players.find((row) => row.id === entry.playerId);
        const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
        return <option key={entry.id} value={entry.playerId}>#{entry.jerseyNumber} {profile?.displayName ?? 'Unknown player'}</option>;
      })}
    </select>
  );
}

function AttendanceCard({ team, roster, gameId, disabled, saving }: { team: Team; roster: RosterEntry[]; gameId: string; disabled: boolean; saving: boolean }) {
  const db = useStore((d) => d);
  const gameRoster = db.gameRosters.find((row) => row.gameId === gameId && row.teamId === team.id);
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">{team.name}</h3>
          <p className="card__sub">{roster.length} rostered players.</p>
        </div>
        {roster.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={disabled}
            onClick={() => {
              roster.forEach((entry) =>
                setGameAttendance({ gameId, teamId: team.id, playerId: entry.playerId, status: 'present' }),
              );
            }}
          >
            Mark all present
          </button>
        )}
      </div>
      {roster.length === 0 ? (
        <div className="empty">No roster yet. Use the roster workspace to import from Directory.</div>
      ) : (
        <div className="stack">
          {roster.map((entry) => {
            const player = db.players.find((row) => row.id === entry.playerId);
            const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
            const status = gameRoster?.attendanceByPlayerId?.[entry.playerId];
            return (
              <div key={entry.id} className="scoresheet-attendance-row">
                <div>
                  <strong>#{entry.jerseyNumber} {profile?.displayName ?? 'Unknown player'}</strong>
                  <div className="muted">
                    {entry.position || player?.position || 'Player'}
                    {!status && ' · attendance not confirmed'}
                  </div>
                </div>
                <div className="scoresheet-attendance-row__actions">
                  {saving && <Loader2 size={14} className="spin" />}
                  {(['present', 'absent', 'late', 'substitute'] as AttendanceStatus[]).map((next) => (
                    <button
                      key={next}
                      className={`chip-btn ${status === next ? 'chip-btn--primary' : ''}`}
                      disabled={disabled}
                      onClick={() => setGameAttendance({ gameId, teamId: team.id, playerId: entry.playerId, status: next })}
                    >
                      {next}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventLog({
  events,
  locked,
  onEditGoal,
  onEditPenalty,
  periodLengthMinutes,
}: {
  events: ScoreEvent[];
  locked: boolean;
  onEditGoal: (event: ScoreEvent) => void;
  onEditPenalty: (event: ScoreEvent) => void;
  periodLengthMinutes: number;
}) {
  const db = getDb();
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">Event log</h3>
          <p className="card__sub">{events.length} event{events.length === 1 ? '' : 's'} captured.</p>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="empty">No score events yet.</div>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead><tr><th>Clock</th><th>Team</th><th>Event</th><th>Player</th><th></th></tr></thead>
            <tbody>
              {events.map((event) => {
                const team = db.teams.find((row) => row.id === event.teamId);
                const player = event.playerId ? db.players.find((row) => row.id === event.playerId) : undefined;
                const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
                return (
                  <tr key={event.id}>
                    <td className="muted">{formatGameClock(event.period, event.gameSecond, periodLengthMinutes)}</td>
                    <td>{team?.shortCode ?? '-'}</td>
                    <td><span className="chip">{eventLabel(event)}</span></td>
                    <td>{profile?.displayName ?? <span className="muted">-</span>}</td>
                    <td style={{ textAlign: 'right' }}>
                      {!locked && (
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                          {event.type === 'goal' && (
                            <button className="btn btn--ghost btn--sm" onClick={() => onEditGoal(event)}>Edit</button>
                          )}
                          {event.type === 'penalty' && (
                            <button className="btn btn--ghost btn--sm" onClick={() => onEditPenalty(event)}>Edit</button>
                          )}
                          <button className="btn btn--ghost btn--sm" onClick={() => deleteScoreEvent(event.id)} aria-label="Delete event">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Checklist({ checklist }: { checklist: ReturnType<typeof buildSubmitChecklist> }) {
  return (
    <div className="stack">
      {checklist.blockers.length === 0 && checklist.warnings.length === 0 && (
        <div className="note note--ok"><ClipboardCheck size={16} /> Ready to submit.</div>
      )}
      {checklist.blockers.map((item) => (
        <div key={item} className="note note--warn"><CircleAlert size={16} /> {item}</div>
      ))}
      {checklist.warnings.map((item) => (
        <div key={item} className="note note--info"><CircleAlert size={16} /> {item}</div>
      ))}
    </div>
  );
}

function toGameSecond(period: number, minuteText: string, secondText: string, periodLengthMinutes: number): number | undefined {
  if (minuteText.trim() === '' && secondText.trim() === '') return undefined;
  const minute = Math.max(0, Number(minuteText || 0));
  const second = Math.min(59, Math.max(0, Number(secondText || 0)));
  if (Number.isNaN(minute) || Number.isNaN(second)) return undefined;
  return clockToGameSecond(period, minute, second, periodLengthMinutes);
}

function formatGameClock(period?: number, gameSecond?: number, periodLengthMinutes = 20): string {
  if (!period && gameSecond === undefined) return '-';
  const clock = gameSecondToClock(gameSecond, periodLengthMinutes);
  const minute = clock?.minute ?? null;
  const second = clock?.second ?? 0;
  return `P${period ?? '?'}${minute === null ? '' : ` ${minute}:${String(second).padStart(2, '0')}`}`;
}

function eventLabel(event: ScoreEvent): string {
  if (event.type === 'goal') {
    if (event.isPowerPlay) return 'power play goal';
    if (event.isShortHanded) return 'short-handed goal';
    if (event.isEmptyNet) return 'empty net goal';
    return 'goal';
  }
  if (event.type === 'penalty') return `${event.penaltyMinutes ?? 2} min ${event.penaltyType ?? 'penalty'}`;
  if (event.type === 'shootout_attempt') {
    const result = event.shootoutMade ?? event.notes === 'made' ? 'goal' : 'save/miss';
    const round = event.shootoutRound ? `R${event.shootoutRound} ` : '';
    return `${round}shootout ${result}`;
  }
  return event.type.replaceAll('_', ' ');
}

function browserLabel(): string {
  if (typeof navigator === 'undefined') return 'Scorekeeper console';
  if (/Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)) return 'Mobile scorer';
  return 'Browser scorer';
}

function tieResolutionGuidance(format: TournamentSettings['scoring']['overtimeFormat'] | undefined): { title: string; body: string; decision?: FinalDecision } | undefined {
  if (format === 'straight_shootout') {
    return {
      title: 'Tie game: go straight to shootout',
      body: 'This event is configured for regular-season shootouts. Record each shooter and goalie, then submit the shootout winner on the final tab.',
      decision: 'shootout',
    };
  }
  if (format === 'overtime_then_shootout') {
    return {
      title: 'Tie game: overtime before shootout',
      body: 'This event is configured for overtime first. Set the next period as OT, then use shootout attempts only if the game remains tied.',
      decision: 'overtime',
    };
  }
  if (format === 'continuous_overtime') {
    return {
      title: 'Tie game: continuous overtime',
      body: 'This event is configured for continuous overtime. Continue scoring in the next OT period until a winner is recorded.',
      decision: 'overtime',
    };
  }
  return undefined;
}

async function archiveFinalGame(gameId: string): Promise<void> {
  const response = await fetch('/api/scoring/archive', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gameId }),
  });
  if (!response.ok) throw new Error(`archive failed: ${response.status}`);
}
